import React, { useEffect, useRef, useState, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, GoogleMap } from "@react-google-maps/api";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import Confirmation from "../../components/confirmation/Confirmation";
import { toast } from "react-toastify";
import { LineWave } from "react-loader-spinner";
import http from "../../utils/http";
import {
  fetchStoreById,
  createStoreLocation,
  updateStoreLocation,
} from "../../redux/slices/storeLocationSlice";
import { DEFAULT_STORE_IMAGE, storeLocationValidationSchema } from "../../utils/constants";

// Roles and validation schema imported from constants

const defaultValues = {
  title: "",
  locationName: "",
  storePhoneNo: "",
  storeUrl: "",
  content: "",
};

const fieldClass =
  "mt-1 block w-full px-3 py-2 bg-[#372F5B] rounded-md shadow-sm focus:outline-none focus:ring focus:ring-red-500 focus:ring-opacity-50 text-white";

const mapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1d1b31" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
];

const CreateStoreLocation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { isLoading } = useSelector((state) => state.storeLocationReducer);

  const [initialValues, setInitialValues] = useState(defaultValues);
  const [coords, setCoords] = useState({ lat: "", lng: "" });
  const [coordsError, setCoordsError] = useState("");
  const [storeImage, setStoreImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const imageInputRef = useRef(null);

  // Volunteers state — managed outside Formik
  const [volunteers, setVolunteers] = useState([]);
  const [volunteerSearch, setVolunteerSearch] = useState("");
  const [volunteerSuggestions, setVolunteerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const fetchVolunteerSuggestions = useCallback(async (search) => {
    if (!search.trim()) { setVolunteerSuggestions([]); setShowSuggestions(false); return; }
    try {
      const res = await http.get(`misc/volunteers?search=${encodeURIComponent(search)}`);
      const pool = res?.data?.data || [];
      const filtered = pool.filter(v => !volunteers.find(sel => sel.volunteerEmail === v.volunteerEmail));
      setVolunteerSuggestions(filtered);
      setShowSuggestions(true);
    } catch { setVolunteerSuggestions([]); }
  }, [volunteers]);

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const volunteerSearchRef = useRef(null);

  const fetchedRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!id || fetchedRef.current) return;
    fetchedRef.current = true;
    dispatch(fetchStoreById(id)).then((res) => {
        const data = res?.payload?.data;
        if (data) {
          setInitialValues({
            title: data.title || "",
            locationName: data.locationName || "",
            storePhoneNo: data.storePhoneNo || "",
            storeUrl: data.storeUrl || "",
            content: data.content || "",
          });
          setCoords({
            lat: data.locationLat || "",
            lng: data.locationLng || "",
          });
          setVolunteers(data.volunteers || []);
          if (data.storeImage) setImagePreview(`${data.storeImage}`);
        }
      });
  }, [id, dispatch]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (volunteerSearchRef.current && !volunteerSearchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleVolunteerSearch = (value) => {
    setVolunteerSearch(value);
    fetchVolunteerSuggestions(value);
  };

  const clearVolunteerSearch = () => {
    setVolunteerSearch("");
    setVolunteerSuggestions([]);
    setShowSuggestions(false);
  };

  const handleAddVolunteer = (volunteer) => {
    setVolunteers((prev) => [...prev, volunteer]);
    setVolunteerSearch("");
    setVolunteerSuggestions([]);
    setShowSuggestions(false);
  };

  const handleRemoveVolunteer = async (email) => {
    const updated = volunteers.filter((v) => v.volunteerEmail !== email);
    setVolunteers(updated);
    setRemoveTarget(null);
    if (id) {
      try {
        await http.patch(`store/${id}/volunteers`, { volunteers: updated });
        toast.success("Volunteer removed");
      } catch {
        toast.error("Failed to update volunteers on server");
      }
    }
  };

  // Map handlers
  useEffect(() => {
    if (!mapRef.current || !coords.lat || !coords.lng) return;
    const position = { lat: parseFloat(coords.lat), lng: parseFloat(coords.lng) };
    if (markerRef.current) {
      markerRef.current.setPosition(position);
      mapRef.current.panTo(position);
    }
  }, [coords]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
    if (coords.lat && coords.lng) {
      const position = { lat: parseFloat(coords.lat), lng: parseFloat(coords.lng) };
      markerRef.current = new window.google.maps.Marker({ position, map });
    }
  };

  const handlePlaceChanged = (setFieldValue) => {
    const place = autocompleteRef.current.getPlace();
    if (!place?.geometry) return;
    const lat = place.geometry.location.lat().toString();
    const lng = place.geometry.location.lng().toString();
    const address = place.formatted_address || place.name || "";
    setCoords({ lat, lng });
    setCoordsError("");
    setFieldValue("locationName", address);
    if (mapRef.current) {
      const position = { lat: parseFloat(lat), lng: parseFloat(lng) };
      if (markerRef.current) {
        markerRef.current.setPosition(position);
        mapRef.current.panTo(position);
      } else {
        markerRef.current = new window.google.maps.Marker({ position, map: mapRef.current });
      }
    }
  };

  const uploadImage = async (storeId) => {
    if (!storeImage) return;
    const formData = new FormData();
    formData.append('image', storeImage);
    await http.post(`store/${storeId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  };

  const handleSubmit = async (values) => {
    if (!coords.lat || !coords.lng) {
      setCoordsError("Please select a location from the search suggestions.");
      return;
    }
    const payload = {
      title: values.title.trim(),
      locationName: values.locationName.trim(),
      storePhoneNo: values.storePhoneNo.trim(),
      storeUrl: values.storeUrl.trim(),
      locationLat: coords.lat,
      locationLng: coords.lng,
      content: values.content.trim(),
      volunteers,
    };

    setIsSaving(true);
    try {
      if (id) {
        const res = await dispatch(updateStoreLocation({ id, body: payload }));
        if (res?.payload?.code === 200) {
          await uploadImage(id);
          toast.success("Store Location Updated Successfully");
          navigate("/panel/storelocations");
        } else {
          toast.error("Something Went Wrong");
        }
      } else {
        const res = await dispatch(createStoreLocation(payload));
        if (res?.payload?.code === 200) {
          const newId = res.payload?.data?._id;
          if (newId) await uploadImage(newId);
          toast.success("Store Location Created Successfully");
          navigate("/panel/storelocations");
        } else {
          toast.error("Something Went Wrong");
        }
      }
    } catch {
      toast.error("Something Went Wrong");
    } finally {
      setIsSaving(false);
    }
  };

  const hasCoords = Boolean(coords.lat && coords.lng);

  return (
    <div className="pb-[60px]">
    <div className="flex items-start">

      <Link to="/panel/storelocations" className="inline-block text-white pb-5 text-3xl">
        <IoMdArrowRoundBack />
      </Link>
    </div>

      <div className="max-w-7xl mx-auto p-6 bg-[#211849] shadow-md rounded-lg text-left text-white">
        <h2 className="text-2xl font-bold mb-8">
          {id ? "Edit Store Location" : "Add Store Location"}
        </h2>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={storeLocationValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue }) => (
            <Form>
              {/* Title + Phone */}
              <div className="flex flex-wrap -mx-2 mb-4">
                <div className="w-full sm:w-1/2 px-2">
                  <label htmlFor="title" className="block text-sm font-medium">Store Title <span className="text-red-500">*</span></label>
                  <Field type="text" id="title" name="title" className={fieldClass} />
                  <ErrorMessage name="title" component="p" className="text-red-500 text-sm mt-1" />
                </div>
                <div className="w-full sm:w-1/2 px-2">
                  <label htmlFor="storePhoneNo" className="block text-sm font-medium">Phone Number <span className="text-red-500">*</span></label>
                  <Field
                    type="tel"
                    id="storePhoneNo"
                    name="storePhoneNo"
                    className={fieldClass}
                    onKeyDown={(e) => {
                      // Allow: backspace, delete, tab, escape, enter, arrows, +, -, (, ), space
                      const allowed = ['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','+','-','(',')',' '];
                      if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <ErrorMessage name="storePhoneNo" component="p" className="text-red-500 text-sm mt-1" />
                </div>
              </div>

              {/* Location search */}
              <div className="mb-4">
                <label htmlFor="locationName" className="block text-sm font-medium">Location <span className="text-red-500">*</span></label>
                <Autocomplete
                  onLoad={(ref) => (autocompleteRef.current = ref)}
                  onPlaceChanged={() => handlePlaceChanged(setFieldValue)}
                >
                  <Field
                    type="text"
                    id="locationName"
                    name="locationName"
                    placeholder="Search for a location..."
                    className={fieldClass}
                  />
                </Autocomplete>
                <ErrorMessage name="locationName" component="p" className="text-red-500 text-sm mt-1" />
                {coordsError && <p className="text-red-500 text-sm mt-1">{coordsError}</p>}
                {hasCoords && (
                  <p className="text-gray-400 text-xs mt-1">
                    Lat: {coords.lat} &nbsp;|&nbsp; Lng: {coords.lng}
                  </p>
                )}
              </div>

              {/* Store URL */}
              <div className="mb-4">
                <label htmlFor="storeUrl" className="block text-sm font-medium">Store URL <span className="text-red-500">*</span></label>
                <Field type="text" id="storeUrl" name="storeUrl" className={fieldClass} />
                <ErrorMessage name="storeUrl" component="p" className="text-red-500 text-sm mt-1" />
              </div>

              {/* Content */}
              <div className="mb-6">
                <label htmlFor="content" className="block text-sm font-medium">Content / Description <span className="text-red-500">*</span></label>
                <Field as="textarea" rows="3" id="content" name="content" className={fieldClass} />
                <ErrorMessage name="content" component="p" className="text-red-500 text-sm mt-1" />
              </div>

              {/* Store Image */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">Store Image</label>
                <div
                  className="border-2 border-dashed border-[#453a6e] rounded-md p-4 text-center cursor-pointer hover:border-red-500 transition-colors"
                  onClick={() => imageInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img src={imagePreview} alt="Store" className="max-h-48 rounded-md mx-auto object-cover" />
                      <button
                        type="button"
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                        onClick={async (e) => {
                          e.stopPropagation();
                          setStoreImage(null);
                          setImagePreview(null);
                          if (id) {
                            try {
                              await http.put(`store/update/${id}`, { storeImage: null });
                            } catch { /* silent */ }
                          }
                        }}
                      >✕</button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <img src={DEFAULT_STORE_IMAGE} alt="Default" className="max-h-24 rounded-md opacity-40 object-cover" />
                      <p className="text-gray-400 text-sm">Click to upload store image (JPG, PNG, WEBP, GIF · max 5MB)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
                    if (!allowed.includes(file.type)) {
                      toast.error("Only JPG, PNG, WEBP or GIF images are allowed");
                      e.target.value = "";
                      return;
                    }
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error("Image must be under 5MB");
                      e.target.value = "";
                      return;
                    }
                    setStoreImage(file);
                    setImagePreview(URL.createObjectURL(file));
                  }}
                />
              </div>

              {/* Volunteers */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">Volunteers</label>

                {/* Search input */}
                <div className="relative mb-4" ref={volunteerSearchRef}>
                  <div className="relative">
                    <AiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={volunteerSearch}
                      onChange={(e) => handleVolunteerSearch(e.target.value)}
                      onFocus={() => volunteerSearch && setShowSuggestions(true)}
                      placeholder="Search volunteers by name or email..."
                      className="block w-full pl-9 pr-10 py-2 bg-[#372F5B] rounded-md shadow-sm focus:outline-none focus:ring focus:ring-red-500 focus:ring-opacity-50 text-white text-sm"
                    />
                    {volunteerSearch && (
                      <button
                        type="button"
                        onClick={clearVolunteerSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        title="Clear search"
                      >
                        <AiOutlineClose className="text-sm" />
                      </button>
                    )}
                  </div>
                  {showSuggestions && volunteerSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-[#2a2255] border border-[#453a6e] rounded-md shadow-lg max-h-52 overflow-y-auto">
                      {volunteerSuggestions.map((v, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between px-4 py-2 hover:bg-[#372F5B] gap-2"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{v.displayName}</p>
                            <p className="text-gray-400 text-xs truncate">{v.volunteerEmail}</p>
                          </div>
                          <button
                            type="button"
                            className="text-green-400 text-xs whitespace-nowrap hover:text-green-300"
                            onMouseDown={() => handleAddVolunteer({ ...v })}
                          >
                            + Add
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {showSuggestions && volunteerSearch && volunteerSuggestions.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-[#2a2255] border border-[#453a6e] rounded-md px-4 py-3">
                      <p className="text-gray-400 text-sm">No volunteers found</p>
                    </div>
                  )}
                </div>

                {/* Volunteers table */}
                {volunteers.length > 0 ? (
                  <div className="rounded-md overflow-hidden border border-[#372F5B]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#372F5B] text-gray-300 text-left">
                          <th className="px-4 py-3 font-medium">Name</th>
                          <th className="px-4 py-3 font-medium">Role</th>
                          <th className="px-4 py-3 font-medium">Email</th>
                          <th className="px-4 py-3 font-medium w-16 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {volunteers.map((v, index) => (
                          <tr
                            key={index}
                            className="border-t border-[#372F5B] hover:bg-[#2a2255] transition-colors"
                          >
                            <td className="px-4 py-3 text-white">{v.displayName}</td>
                            <td className="px-4 py-3 text-gray-300">{v.roles}</td>
                            <td className="px-4 py-3 text-gray-400">{v.volunteerEmail}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => setRemoveTarget(v.volunteerEmail)}
                                className="text-red-500 hover:text-red-400 transition-colors"
                                title="Remove volunteer"
                              >
                                <MdDelete className="text-lg" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-3">
                    No volunteers added yet. Search above to add one.
                  </p>
                )}
              </div>

              <div className="flex justify-end items-center gap-2">
                <button
                  type="button"
                  className="text-white bg-gray-600 px-4 py-2 rounded-md mt-4 hover:bg-gray-700"
                  onClick={() => navigate("/panel/storelocations")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="text-white bg-red-500 px-4 py-2 rounded-md mt-4 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSaving ? "Saving..." : (id ? "Update Location" : "Create Location")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {(isLoading || isSaving) && (
        <div className="loader">
          <LineWave color={"#fff"} loading={isLoading} size={150} />
        </div>
      )}

      {/* Map — renders as soon as coords are available */}
      {hasCoords && (
        <div className="max-w-7xl mx-auto mt-4 rounded-lg overflow-hidden">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "380px" }}
            center={{ lat: parseFloat(coords.lat), lng: parseFloat(coords.lng) }}
            zoom={15}
            onLoad={handleMapLoad}
            options={{ styles: mapStyles, zoomControl: true }}
          />
        </div>
      )}

      {removeTarget && (
        <Confirmation
          message="remove this volunteer"
          buttonText="Remove"
          confirmDelete={() => handleRemoveVolunteer(removeTarget)}
          cancelDelete={() => setRemoveTarget(null)}
        />
      )}
    </div>
  );
};

export default CreateStoreLocation;
